import { Drawer, Descriptions, Typography, Skeleton, Divider } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import {
  CustomerName,
  XAICallStatusTag,
} from "@lib/components/MiniComponents/Others";
import styled from "styled-components";
import dayjs from "dayjs";
import { XAILead, XAICall } from "@lib/types/xAI";
import { useQuery } from "@tanstack/react-query";
import { apiGetXAICalls, apiGetXAILead } from "@lib/api/xAI";
import { useState } from "react";
import { XAICallDrawer } from "@lib/components/PageComponents/XAICall";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { XAILeadCallsList } from "./XAILeadCallsList";
import { AppCard, AppJson } from "@lib/components/AppComponents";

const StyledDescriptions = styled(Descriptions)`
  th,
  td {
    padding: 5px !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-descriptions-item-label {
    width: 200px;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  item: XAILead | null;
};

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 9 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

export const XAILeadDrawer = (props: Props) => {
  const { open, onClose, item } = props;
  const { messages } = useIntl();
  const [selectedCall, setSelectedCall] = useState<XAICall | null>(null);

  const leadDataQuery = useQuery({
    queryFn: () => apiGetXAILead(item?.id),
    queryKey: ["xai-lead", item?.id],
    enabled: !!item?.id && open,
  });
  const leadData = leadDataQuery?.data?.data;
  const isLoading = leadDataQuery?.isLoading || leadDataQuery?.isFetching;

  const callsQuery = useQuery({
    queryKey: [
      "xai-calls",
      leadData?.id,
      JSON.stringify(leadDataQuery?.dataUpdatedAt),
    ],
    queryFn: () =>
      apiGetXAICalls({
        page: 1,
        limit: 100,
        sort: "createdAt,DESC",
        join: ["customer", "lead"],
        filter: leadData?.id ? [`leadId||$eq||${leadData?.id}`] : [],
      }),
    enabled: !!leadData?.id && open,
  });

  const callsData = callsQuery.data?.data?.data || [];

  return (
    <>
      <StyledDrawer
        title={
          <FormattedMessage
            id={"common.previewTitle"}
            values={{ title: messages["common.xaiLead"] as string }}
          />
        }
        onClose={onClose}
        open={open}
        width={selectedCall ? 1100 : 800}
        id="xai-lead-drawer"
        extra={
          <ActionButton
            type="refresh"
            size="small"
            query={leadDataQuery}
            onClick={() => leadDataQuery?.refetch()}
          />
        }
      >
        {isLoading ? (
          <SkeletonContent />
        ) : (
          <>
            <StyledDescriptions bordered column={1}>
              <Descriptions.Item label="ID">
                <Typography.Text copyable={{ text: leadData?.id }}>
                  {leadData?.id}
                </Typography.Text>
              </Descriptions.Item>

              <Descriptions.Item label={<FormattedMessage id="common.name" />}>
                {leadData?.firstName} {leadData?.lastName}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.customer" />}
              >
                <CustomerName customer={leadData?.customer} link />
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.lastVisit" />}
              >
                {leadData?.lastVisit || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.createdAt" />}
              >
                {dayjs(leadData?.createdAt).format("DD/MM/YYYY HH:mm:ss")}
              </Descriptions.Item>

              <Descriptions.Item label={<FormattedMessage id="common.agent" />}>
                {leadData?.agent || "-"}
              </Descriptions.Item>

              <Descriptions.Item label={<FormattedMessage id="common.brand" />}>
                {leadData?.brand || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.businessUnit" />}
              >
                {leadData?.businessUnit || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.lastIpAddress" />}
              >
                {leadData?.lastIpAddress || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.lastCountry" />}
              >
                {leadData?.lastCountry || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.callStatus" />}
              >
                <XAICallStatusTag status={leadData?.callStatus} />
              </Descriptions.Item>

              <Descriptions.Item label={<FormattedMessage id="common.error" />}>
                {leadData?.error || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.aiModelId" />}
              >
                {leadData?.extraData?.aiModelId || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.brokerName" />}
              >
                {leadData?.extraData?.brokerName || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.preferredPhoneNumber" />}
              >
                {leadData?.extraData?.preferredPhoneNumber || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.talkingPoints" />}
              >
                {leadData?.talkingPoints || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.clientSummary" />}
              >
                {leadData?.clientSummary || "-"}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.summary" />}
              >
                {leadData?.summary || "-"}
              </Descriptions.Item>
            </StyledDescriptions>

            <Divider>
              <FormattedMessage id="common.extraData" />
            </Divider>
            <AppCard borderFull collapse={false}>
              <div style={{ padding: 16 }}>
                <AppJson src={leadData?.extraData || {}} />
              </div>
            </AppCard>

            <Divider>
              <FormattedMessage id="common.calls" />
            </Divider>

            <XAILeadCallsList
              callsData={callsData}
              isLoading={callsQuery.isLoading || callsQuery.isFetching}
              onCallClick={setSelectedCall}
            />
          </>
        )}
      </StyledDrawer>

      <XAICallDrawer
        open={!!selectedCall}
        onClose={() => setSelectedCall(null)}
        item={selectedCall}
      />
    </>
  );
};
